<template>
  <v-card class="pa-4">
    <h2>Outstanding By Adjuster (By Date)</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <!-- Branch droplist -->
        <v-select
          v-model="selectedBranch"
          :items="branchOptions"
          item-text="name"
          item-value="id"
          label="Branch"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <!-- Adjuster droplist -->
        <v-select
          v-model="selectedAdjuster"
          :items="filteredAdjusterOptions"
          item-text="username"
          item-value="id"
          label="Adjuster"
          dense
          outlined
          required
          :disabled="!selectedBranch"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedClass"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <!-- Date picker -->
        <v-menu v-model="menuDate" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDate"
              label="Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedDate"
            @change="menuDate = false"
            :max="today"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!selectedDate"
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :loading="exporting"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <div
        class="mb-2"
        style="font-style: italic; font-size: 1.1em; text-align: center"
      >
        {{ criteriaText }}
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th>Insurer</th>
            <th>Within TAT</th>
            <th>TAT Breach</th>
            <th>Total</th>
            <th>Breach %</th>
          </tr>
        </thead>
        <tbody>
          <!-- Duplicate TOTAL row immediately after header -->
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.withinTat }}</strong>
            </td>
            <td class="red--text font-weight-bold">
              <strong>{{ totals.breachedTat }}</strong>
            </td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
            <td>
              <strong>{{ totals.breachPercent }}</strong>
            </td>
          </tr>
          <!-- Main data rows -->
          <template v-for="row in reportData">
            <tr :key="row.insurerCode">
              <td>
                <strong>{{ row.insurerCode }}</strong>
              </td>
              <td>{{ row.withinTat }}</td>
              <td class="red--text font-weight-bold">{{ row.breachedTat }}</td>
              <td>{{ row.total }}</td>
              <td>
                <span :style="breachCellStyle(row.percent)" class="px-2 py-1">
                  {{ row.percent }}
                </span>
              </td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.insurerCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.insurerCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.withinTat }}</td>
              <td>{{ detail.breachedTat }}</td>
              <td>{{ detail.total }}</td>
              <td>
                <span
                  :style="breachCellStyle(detailPercent(detail))"
                  class="px-2 py-1"
                >
                  {{ detailPercent(detail) }}
                </span>
              </td>
            </tr>
          </template>
          <!-- Bottom TOTAL row -->
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.withinTat }}</strong>
            </td>
            <td class="red--text font-weight-bold">
              <strong>{{ totals.breachedTat }}</strong>
            </td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
            <td>
              <strong>{{ totals.breachPercent }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select department, classification, and date, then click "Generate
      Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import BranchDataService from "../../services/BranchDataService";
import MemberDataService from "../../services/MemberDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "OutstandingInsurer",
  data() {
    return {
      selectedBranch: "all",
      branchOptions: [],
      selectedAdjuster: "all",
      adjusterOptions: [],
      selectedDept: "all",
      deptOptions: [],
      selectedClass: "all",
      classOptions: [],
      filteredClassOptions: [],
      selectedDate: null,
      menuDate: false,
      reportData: [],
      totals: {
        withinTat: 0,
        breachedTat: 0,
        total: 0,
        breachPercent: "0.00",
      },
      exporting: false,
      noDataMessage: "",
      criteriaText: "",
      today: moment().format("YYYY-MM-DD"),
    };
  },
  computed: {
    filteredAdjusterOptions() {
      if (!this.selectedBranch || this.selectedBranch === "all") {
        return [{ id: "all", username: "ALL ADJUSTERS" }];
      }
      // Find selected branch object to get brCode
      const branchObj = this.branchOptions.find(
        (b) => b.id === this.selectedBranch,
      );
      if (!branchObj || !branchObj.brCode) {
        return [{ id: "all", username: "ALL ADJUSTERS" }];
      }
      const brCode = branchObj.brCode;
      // Filter adjusters by branch code
      const filtered = this.adjusterOptions
        .filter(
          (adj) =>
            Array.isArray(adj.branches) &&
            adj.branches.some((b) => b.brCode === brCode),
        )
        .map((adj) => ({
          ...adj,
          username: (adj.username || "").toUpperCase(),
        }))
        .sort((a, b) => a.username.localeCompare(b.username));
      return [{ id: "all", username: "ALL ADJUSTERS" }, ...filtered];
    },
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
    // Reset adjuster when branch changes
    selectedBranch() {
      this.selectedAdjuster = "all";
    },
  },
  methods: {
    async loadBranches() {
      const res = await BranchDataService.getAll();
      this.branchOptions = [
        { id: "all", name: "ALL BRANCHES" },
        ...(res.data || [])
          .map((b) => ({
            ...b,
            name: (b.name || "").toUpperCase(),
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ];
    },
    async loadAdjusters() {
      const res = await MemberDataService.getAll();
      this.adjusterOptions = (res.data || [])
        .filter(
          (item) =>
            item.roles &&
            item.roles.some((r) => r.roleCode === "adjuster") &&
            item.active,
        )
        .map((item) => ({
          ...item,
          username: (item.username || "").toUpperCase(),
        }))
        .sort((a, b) => a.username.localeCompare(b.username));
    },
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [
        { id: "all", name: "ALL DEPARTMENTS" },
        ...res.data,
      ].sort((a, b) => a.name.localeCompare(b.name));
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      // Always ensure ALL CLASSIFICATIONS is present and at the top
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ].sort((a, b) => {
        if (a.id === "all") return -1;
        if (b.id === "all") return 1;
        return a.subCode.localeCompare(b.subCode);
      });
    },
    filterClassifications(deptId) {
      // Only show ALL CLASSIFICATIONS if ALL DEPARTMENTS is selected
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all",
        );
      } else {
        // Filter for selected department, but always include ALL CLASSIFICATIONS
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        withinTat: 0,
        breachedTat: 0,
        total: 0,
        breachPercent: "0.00",
      };
      this.noDataMessage = "";
      this.criteriaText = "";
      try {
        const params = {
          // branchId: this.selectedBranch, // not needed for backend
          adjusterId: this.selectedAdjuster,
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          date: this.selectedDate
            ? moment(this.selectedDate).format("YYYY-MM-DD")
            : "",
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/outstanding-by-adjuster`,
          { params },
        );
        this.processReportData(res.data);
        // Use backend criteriaText if available, else fallback
        this.criteriaText = res.data.criteriaText || this.buildCriteriaText();
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const dateStr = moment(this.selectedDate).format("DD MMMM YYYY");
          this.noDataMessage = `No outstanding data found for ${dateStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    buildCriteriaText() {
      let criteria = "Outstanding Assignment";
      let adjusterName = "";
      if (this.selectedAdjuster && this.selectedAdjuster !== "all") {
        const adj = this.adjusterOptions.find(
          (a) => a.id === this.selectedAdjuster,
        );
        adjusterName = adj ? adj.username : this.selectedAdjuster;
      }
      let deptName = "";
      if (this.selectedDept && this.selectedDept !== "all") {
        const dept = this.deptOptions.find((d) => d.id === this.selectedDept);
        deptName = dept ? dept.name : this.selectedDept;
      }
      let className = "";
      if (this.selectedClass && this.selectedClass !== "all") {
        const cls = this.classOptions.find((c) => c.id === this.selectedClass);
        className = cls ? cls.subCode : this.selectedClass;
      }
      let criteriaParts = [];
      criteriaParts.push(
        this.selectedAdjuster && this.selectedAdjuster !== "all"
          ? `for "${adjusterName}"`
          : "for ALL ADJUSTERS",
      );
      criteriaParts.push(
        this.selectedDept && this.selectedDept !== "all"
          ? `in "${deptName}"`
          : "in ALL DEPARTMENT",
      );
      criteriaParts.push(
        this.selectedClass && this.selectedClass !== "all"
          ? `(${className})`
          : "(ALL CLASSIFICATION)",
      );
      criteriaParts.push(
        this.selectedDate
          ? `until "${moment(this.selectedDate).format("DD/MM/YYYY")}"`
          : "",
      );
      return `${criteria} ${criteriaParts.join(" ")}`;
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      let withinTat = 0,
        breachedTat = 0,
        total = 0;
      this.reportData.forEach((row) => {
        withinTat += row.withinTat;
        breachedTat += row.breachedTat;
        total += row.total;
      });
      this.totals.withinTat = withinTat;
      this.totals.breachedTat = breachedTat;
      this.totals.total = total;
      this.totals.breachPercent =
        total > 0 ? ((breachedTat / total) * 100).toFixed(2) : "0.00";
    },
    breachCellStyle(percent) {
      const val = parseFloat(percent);
      if (val > 80) {
        return "background:#FF6600;color:white;border-radius:4px;";
      } else if (val >= 50) {
        return "background:#FFB366;color:black;border-radius:4px;";
      }
      return "";
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          // branchId: this.selectedBranch, // not needed for backend
          adjusterId: this.selectedAdjuster,
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          date: this.selectedDate
            ? moment(this.selectedDate).format("YYYY-MM-DD")
            : "",
        };
        const url =
          `${apiConfig.baseURL}/compliance/outstanding-by-adjuster/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
    detailPercent(detail) {
      return detail.total > 0
        ? ((detail.breachedTat / detail.total) * 100).toFixed(2)
        : "0.00";
    },
  },
  mounted() {
    this.loadBranches();
    this.loadAdjusters();
    this.loadDepartments();
    this.loadClassifications();
  },
};
</script>
